import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, ArrowRight, ImageOff, Share2, Flame } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "../lib/api";
import SEO from "../components/SEO";

// Fire-and-forget share-event ping — never blocks/breaks the share itself.
const pingShareEvent = (storyId, source = "share_button") => {
    api.post(`/impact/stories/${storyId}/share-event`, { source }).catch(() => {});
};

/**
 * /impact — public Impact stories page.
 * Surfaces paid spend-requests with completion notes or after-photos
 * as before/after style impact cards.
 */
const Impact = () => {
    const [stories, setStories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [mostShared, setMostShared] = useState([]);

    useEffect(() => {
        api.get("/impact/stories", { params: { limit: 50 } })
            .then((r) => setStories(r.data || []))
            .catch(() => setStories([]))
            .finally(() => setLoading(false));
        api.get("/impact/most-shared", { params: { days: 30, limit: 4 } })
            .then((r) => setMostShared(r.data?.stories || []))
            .catch(() => setMostShared([]));
    }, []);

    return (
        <div className="bg-[#FDFBF7] min-h-[calc(100vh-160px)]" data-testid="impact-page">
            <SEO
                title="Impact stories · What your donations built"
                description="Real projects, real photos, real outcomes. See the playgrounds repaired, families supported, and classrooms equipped by your gifts to Reddog Community Foundation."
                path="/impact"
                jsonLd={{
                    "@context": "https://schema.org",
                    "@type": "CollectionPage",
                    name: "Reddog Community Foundation — Impact Stories",
                    description: "Verified outcomes from paid foundation spend requests.",
                }}
            />

            <section className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 py-16 lg:py-20">
                <div className="flex flex-col items-center text-center mb-12">
                    <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-[#F2D8D5] rounded-full mb-4">
                        <Sparkles className="w-3 h-3 text-[#E53935]" strokeWidth={2} />
                        <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#E53935]">Impact stories</span>
                    </div>
                    <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-[#1C252E] font-medium tracking-tight">
                        What your donations built
                    </h1>
                    <p className="text-base text-[#4A5568] mt-5 max-w-2xl leading-relaxed">
                        Each story below is a paid spend-request from the foundation's public ledger — same dollar
                        amount, same vendor, plus photos and outcomes attached after the project closed.
                    </p>
                </div>

                {mostShared.length > 0 && (
                    <section className="mb-12" data-testid="impact-most-shared">
                        <div className="flex items-center gap-2 mb-4">
                            <Flame className="w-4 h-4 text-[#E53935]" strokeWidth={2} />
                            <h2 className="font-heading text-xl text-[#1C252E] font-medium">
                                Most shared this month
                            </h2>
                            <span className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#4A5568] bg-[#F4F1EA] rounded-full px-2 py-0.5">
                                Trending
                            </span>
                        </div>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="impact-most-shared-grid">
                            {mostShared.map((s, rank) => {
                                const headline = s.impact_headline || s.title;
                                const photo = s.impact_photo_url || (s.after_photos || [])[0];
                                return (
                                    <Link
                                        key={s.id}
                                        to={`/ledger/spend/${s.id}`}
                                        data-testid={`impact-most-shared-card-${s.id}`}
                                        className="bg-white border border-[#E53935]/30 rounded-2xl overflow-hidden hover:shadow-md transition-all relative group"
                                    >
                                        <div className="absolute top-2 left-2 z-10 flex items-center gap-1 bg-[#1C252E] text-white rounded-full px-2.5 py-1 text-[10px] uppercase tracking-[0.16em] font-bold">
                                            #{rank + 1} · {s.share_count} {s.share_count === 1 ? "share" : "shares"}
                                        </div>
                                        {photo ? (
                                            <img src={photo} alt="" className="w-full h-28 object-cover bg-[#F4F1EA]" />
                                        ) : (
                                            <div className="w-full h-28 bg-[#F4F1EA] flex items-center justify-center">
                                                <ImageOff className="w-6 h-6 text-[#4A5568]/40" strokeWidth={1.5} />
                                            </div>
                                        )}
                                        <div className="p-3">
                                            <h3 className="font-heading text-sm text-[#1C252E] font-medium leading-tight line-clamp-2 group-hover:text-[#E53935]">
                                                {headline}
                                            </h3>
                                        </div>
                                    </Link>
                                );
                            })}
                        </div>
                    </section>
                )}

                {loading ? (
                    <div className="text-center text-[#4A5568] py-20" role="status" data-testid="impact-loading">Loading stories…</div>
                ) : stories.length === 0 ? (
                    <div className="bg-white border border-black/5 rounded-3xl p-12 text-center" data-testid="impact-empty">
                        <ImageOff className="w-10 h-10 text-[#4A5568] mx-auto mb-4" strokeWidth={1.5} />
                        <p className="text-[#1C252E] font-medium mb-2">No impact stories yet.</p>
                        <p className="text-sm text-[#4A5568] mb-6 max-w-md mx-auto">
                            As soon as a foundation project closes and our board attaches before/after photos and outcomes, the story will appear here.
                        </p>
                        <Link to="/ledger" className="inline-flex items-center gap-2 bg-[#1C252E] text-white hover:bg-black rounded-full px-6 py-3 text-sm font-medium transition-all">
                            See the live ledger instead
                            <ArrowRight className="w-4 h-4" strokeWidth={2} />
                        </Link>
                    </div>
                ) : (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="impact-grid">
                        {stories.map((s) => {
                            const headline = s.impact_headline || s.title;
                            const body_txt = s.impact_body || s.completion_notes || s.purpose_public || "";
                            const photo = s.impact_photo_url || (s.after_photos || [])[0] || (s.before_photos || [])[0];
                            const shareUrl = `${window.location.origin}/ledger/spend/${s.id}`;
                            const onShare = async (e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                const data = {
                                    title: `Reddog Foundation · ${headline}`,
                                    text: `$${parseFloat(s.amount || 0).toFixed(0)} funded · ${headline}`,
                                    url: shareUrl,
                                };
                                if (navigator.share) {
                                    try {
                                        await navigator.share(data);
                                        pingShareEvent(s.id, "share_button");
                                        return;
                                    } catch (err) {
                                        if (err?.name === "AbortError") return;
                                    }
                                }
                                try {
                                    await navigator.clipboard.writeText(shareUrl);
                                    pingShareEvent(s.id, "share_button");
                                    toast.success("Link copied — paste anywhere to share");
                                } catch (e2) {
                                    toast.error("Could not copy link");
                                }
                            };
                            return (
                                <div
                                    key={s.id}
                                    data-testid={`impact-card-${s.id}`}
                                    className="bg-white border border-black/5 rounded-2xl overflow-hidden hover:shadow-lg transition-all flex flex-col group relative"
                                >
                                    <Link to={`/ledger/spend/${s.id}`} className="flex-1 flex flex-col">
                                        {photo ? (
                                            <img src={photo} alt="" className="w-full h-44 object-cover bg-[#F4F1EA]" />
                                        ) : (
                                            <div className="w-full h-44 bg-[#F4F1EA] flex items-center justify-center">
                                                <ImageOff className="w-10 h-10 text-[#4A5568]/40" strokeWidth={1.5} />
                                            </div>
                                        )}
                                        <div className="p-5 flex-1 flex flex-col">
                                            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#E53935] mb-2">
                                                ${parseFloat(s.amount || 0).toFixed(2)} · {s.vendor_name || "Vendor"}
                                            </p>
                                            <h3 className="font-heading text-lg text-[#1C252E] font-medium leading-tight mb-2">
                                                {headline}
                                            </h3>
                                            {body_txt && (
                                                <p className="text-sm text-[#4A5568] leading-relaxed line-clamp-3 flex-1">
                                                    {body_txt}
                                                </p>
                                            )}
                                            <p className="text-xs text-[#4A5568] mt-3 inline-flex items-center gap-1 group-hover:text-[#E53935]">
                                                See the ledger entry <ArrowRight className="w-3 h-3" strokeWidth={2} />
                                            </p>
                                        </div>
                                    </Link>
                                    <button
                                        type="button"
                                        onClick={onShare}
                                        aria-label={`Share ${headline}`}
                                        data-testid={`impact-share-${s.id}`}
                                        className="absolute top-3 right-3 bg-white/95 backdrop-blur text-[#1C252E] hover:bg-[#E53935] hover:text-white rounded-full p-2 shadow-md transition-colors"
                                    >
                                        <Share2 className="w-4 h-4" strokeWidth={2} />
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                )}
            </section>
        </div>
    );
};

export default Impact;
